import { DeleteOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Row,
  Select,
  Spin
} from "antd";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createSettingsDataPoint,
  deleteSettingsDataPoint,
  getSettingsDataPoint
} from "../../store/actions/settings.actions";
import { DATE_FORMAT } from "../../Utils/date_utils";
import { OptionGroupSelect } from '../Compare/Components_1v1/OptionGroupSelect';
import { OverrideDatapointField } from "./OverrideDatapointField";
import "./styles.css";
import { 
  PermissionedSelect, 
  PermissionedDatePicker, 
  PermissionedButton 
} from "../PermissionedComponents";
import PermissionViewable from "../PermissionedComponents/PermissionViewable";

const OVERRIDE_DATAPOINT_TYPES = [
  { value: "gps", label: "GPS Data" },
  { value: "forceplate", label: "Forceplate Data" },
  { value: "testing", label: "Testing Data" },
  { value: "nutrition", label: "Nutrition Data" },
];

const { Option } = Select;

export const OverrideDatapoint = () => {
  const dispatch = useDispatch();
  const {
    datapoint,
    loading: { datapoint: loading },
    pending,
    avg,
  } = useSelector(({ settings }) => settings);

  const {
    positions,
    playerGroups: groups,
    players,
		all_metric_types
	} = useSelector(({ team }) => team);

  const [type, setType] = useState("gps");
  const [date, setDate] = useState(moment());
  const [playerPosGroupOptions, setPlayerPosGroupOptions] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([])
  const [metric, setMetric] = useState();
  const [inputs, setInputs] = useState({});

  const emptyData = useMemo(() => {
    if (type === "gps") {
      if(datapoint === null || datapoint === undefined) {
        return true;
      }
      
      if(datapoint?.data?.length > 0 && datapoint?.metrics?.length > 0) {
        // Checking only if assigned metric values are null. If metric id is null meaning it is a derived metric. 
        // Filtering such derived metrics to get actual assigned metrics and checking if the data exists for those metrics.
        const filteredMetrics = datapoint?.metrics.filter(({ metric_id }) => metric_id !== undefined);
        return filteredMetrics?.every(({ key }) => datapoint?.data[0][key] === null);
      };  
      
      return true;
    }
    else {
      if(datapoint === null || datapoint === undefined) {
        return true;
      }

      return false
    }      
  }, [datapoint]);

  useEffect(() => {
    if (type === "gps") {
      setInputs(datapoint?.data?.[0] || {});
    } else {
      if (metric?.unique_identifier) {
        setInputs({ [metric.unique_identifier]: datapoint });
      }
    }
  }, [datapoint, metric]);

  const handleSearchDatapoint = () => {
    if (!date) return;
    if (type !== "gps" && !metric) return;
    dispatch(
      getSettingsDataPoint(
        type,
        date?.format(DATE_FORMAT),
        selectedPlayers,
        metric
      )
    );
  };

  useEffect(() => {
    handleSearchDatapoint();
  }, [type, date, selectedPlayers, metric]);

  useEffect(() => {
    setInputs({});
    setMetric();
  }, [type]);

  useEffect(() => {
    let groupOptions = [], metricObj = {};
    metricObj["label"] = <span>Team</span>;
    metricObj["title"] = "Team";
    metricObj["options"] = [{
      label: <span>Full Team</span>,
      value: "full-team",
      key: "full-team"
    }];
    
    groupOptions.push(metricObj);

    if (players.length > 0) {
      groupOptions.push(getOptionGroup("Players", players, "player"));
    }

    if (positions.length > 0) {
      groupOptions.push(getOptionGroup("Positions", positions, "pos"));
    }

    if (groups.length > 0) {
      groupOptions.push(getOptionGroup("Groups", groups, "group"));
    }

    setPlayerPosGroupOptions(groupOptions);
    setSelectedPlayers([]);
  }, [positions, groups, players])

  const handleChangeInput = (key) => (value) => {
    setInputs({ ...inputs, [key]: +value });
  };

  const handleCreateDatapoint = () => {
    if (!selectedPlayers.length || !date) return;
    if (type === "gps") {
      if ("total_time" in inputs && (inputs["total_time"] === null || inputs["total_time"] === undefined)) {
        inputs["total_time"] = 0;
      }
      if ("session_RPE" in inputs && (inputs["session_RPE"] === null || inputs["session_RPE"] === undefined)) {
        inputs["session_RPE"] = 0;
      }
    }
    dispatch(
      createSettingsDataPoint(
        type,
        date.format(DATE_FORMAT),
        selectedPlayers,
        inputs,
        metric
      )
    );
  };

  const handleDeleteDatapoint = () => {
    if (!selectedPlayers.length || !date) return;
    dispatch(
      deleteSettingsDataPoint(type, date.format(DATE_FORMAT), selectedPlayers, metric)
    );
  };

  const actionsDisabled = useMemo(() => {
    return !selectedPlayers.length || !date;
  }, [date, selectedPlayers]);

  const fillWithAverage = (type) => (key, identifier) => {
    if (avg[type] && avg[type][key] !== undefined)
      setInputs({ ...inputs, [type === "gps" ? key : identifier]: avg[type][key]?.toFixed(2) });
  };

  const revertChange = (key, identifier) => {
    const data = datapoint.data?.[0] || {};
    setInputs({ ...inputs, [type === "gps" ? key : identifier]: data[key] || datapoint });
  };

  const getOptionGroup = (optionGroupName, JSONOptions, metric_type) => {
    let metricObj = {}, metricOps = [];
    metricObj["label"] = <span>{optionGroupName}</span>;
    metricObj["title"] = optionGroupName;
    
    for (let i = 0; i < JSONOptions.length; i++) {
      const metric = JSONOptions[i];
      if (["gps", "forceplate", "testing", "wellness"].includes(metric_type)) {
        metricOps.push({
          label: <span>{metric["name"]}</span>,
          value: `${metric_type}-${metric["metric_id"]}`,
          key: metric["metric_id"]
        });
      }
      else if (metric_type === "player") {
        metricOps.push({
          label: <span>{`${metric["first_name"]} ${metric["last_name"]}`}</span>,
          value: `${metric_type}-${metric["player_id"]}`,
          key: metric["player_id"]
        });
      }
      else if (metric_type === "pos") {
        metricOps.push({
          label: <span>{metric}</span>,
          value: `${metric_type}-${metric}`,
          key: metric
        });
      }
      else if (metric_type === "group") {
        metricOps.push({
          label: <span>{metric["name"]}</span>,
          value: `${metric_type}-${metric["player_group_id"]}`,
          key: metric["player_group_id"]
       });
      }
    }

    if (metric_type === "gps") {
      metricObj["options"] = metricOps;
    } else {
      metricObj["options"] = metricOps.sort((a, b) => {
        const valueA = a?.label?.props?.children || "";
        const valueB = b?.label?.props?.children || "";
        return valueA.localeCompare(valueB);
      });
    }
    return metricObj;
  }

  const handleOptionGroupChange = (value) => {
    setSelectedPlayers(value);
  }

  return (
    <PermissionViewable 
      permissionCheck="OverrideDataPoints"
      permissionMessage="You don't have permission to override datapoints"
      tooltipMessage="Datapoint override requires special permissions"
    >
      <Row gutter={32}>
        <Col span={10}>
          <div className="team-settings-input-wrap">
            <div className="team-settings-input-label">Select Data Type</div>
            <PermissionedSelect
              placeholder="Choose Data Type"
              showSearch
              optionFilterProp="value"
              value={type}
              className="team-settings-input"
              onChange={setType}
              permissionCheck="OverrideDataPoints"
            >
              {OVERRIDE_DATAPOINT_TYPES.map((type) => (
                <PermissionedSelect.Option
                  key={type.value}
                  value={type.value}
                  disabled={type.disabled}
                >
                  {type.label}
                </PermissionedSelect.Option>
              ))}
            </PermissionedSelect>
          </div>
          <div className="team-settings-input-wrap">
            <div className="team-settings-input-label">Select Date</div>
            <PermissionedDatePicker
              className="team-settings-input"
              value={date}
              onChange={setDate}
              permissionCheck="OverrideDataPoints"
            />
          </div>
          <div className="team-settings-input-wrap">
            <div className="team-settings-input-label">Select Players</div>
            <div className="player-group-pos-container">
              <OptionGroupSelect 
                className="player-group-pos-left" 
                selectedOptionVal={selectedPlayers}
                options={playerPosGroupOptions}
                placeholder="Select Players"
                width={500}
                handleOptionGroupChange={handleOptionGroupChange}
              />
            </div>
          </div>
          {type !== "gps" && (
            <div className="team-settings-input-wrap">
              <div className="team-settings-input-label">Select Metric</div>
              <PermissionedSelect
                placeholder="Choose Metric"
                value={metric}
                className="team-settings-input"
                onChange={(value) =>
                  setMetric(
                    all_metric_types[type]?.find(({ unique_identifier }) => value === unique_identifier)
                  )
                }
                permissionCheck="OverrideDataPoints"
              >
                {all_metric_types[type]?.map((metric) => (
                  <PermissionedSelect.Option key={metric.unique_identifier} value={metric.unique_identifier}>
                    {metric.name}
                  </PermissionedSelect.Option>
                ))}
              </PermissionedSelect>
            </div>
          )}
          <Divider />
          <Spin tip="Loading..." spinning={loading}>
            {type === "gps" && selectedPlayers?.length
              ? datapoint?.metrics?.map((metric) => {
                  return (
                    <OverrideDatapointField
                      metric={metric}
                      key={metric.key}
                      avg={avg}
                      value={inputs[metric.key]}
                      handleChange={handleChangeInput(metric.key)}
                      fillWithAverage={fillWithAverage}
                      revertChange={revertChange}
                      type={type}
                      metricIdentifier={metric.key}
                    />
                  );
                })
              : !!metric && !!selectedPlayers?.length && (
                  <OverrideDatapointField
                    metric={metric}
                    avg={avg}
                    value={inputs[metric.unique_identifier]}
                    handleChange={handleChangeInput(metric.unique_identifier)}
                    fillWithAverage={fillWithAverage}
                    revertChange={revertChange}
                    type={type}
                    metricIdentifier={metric.unique_identifier}
                  />
                )}
            { selectedPlayers?.length ? (
              <Row gutter={16}>
              <Col span={12}>
                <div className="team-settings-input-wrap">
                  <PermissionedButton
                    icon={<SaveOutlined />}
                    type="primary"
                    className="team-settings-button-input"
                    onClick={handleCreateDatapoint}
                    loading={pending.create}
                    disabled={actionsDisabled}
                    permissionCheck="OverrideDataPoints"
                  >
                    Save Datapoint
                  </PermissionedButton>
                </div>
              </Col>
              { !!selectedPlayers?.length && (
                <Col span={12}>
                  <div className="team-settings-input-wrap">
                    <PermissionedButton
                      icon={<DeleteOutlined />}
                      type="danger"
                      className="team-settings-button-input"
                      onClick={handleDeleteDatapoint}
                      disabled={actionsDisabled}
                      loading={pending.delete}
                      permissionCheck="OverrideDataPoints"
                    >
                      Delete Datapoint
                    </PermissionedButton>
                  </div>
                </Col>
              )}
            </Row>
            ) : null}
          </Spin>
        </Col>
      </Row>
    </PermissionViewable>
  );
};
